/**
 * Conversation-analysis hooks — React Query wrappers over the admin API
 * (backend/app/routers/conversation_analysis.py, SA-211). snake_case →
 * camelCase for top-level fields happens here (house convention, mirrors
 * useReplayLab.ts); nested payloads pass through verbatim — see
 * types/conversationAnalysis.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type {
  AnalysisReport,
  AnalysisRunCreateBody,
  AnalysisRunDetail,
  AnalysisRunFilters,
  AnalysisRunStats,
  AnalysisRunSummary,
  AnalysisTrends,
  TrendBucket,
  TrendsParams,
} from '@/types/conversationAnalysis'

/** Poll the runs list while any run is executing, then quiesce. Terminal
 * statuses are `completed` and `error`; the backend presents stale
 * `running` rows (>30 min) as `error` on read, so polling always ends. */
export function runsRefetchInterval(
  rows: { status: string }[] | undefined,
): number | false {
  return rows?.some((r) => r.status === 'running') ? 2000 : false
}

/** Poll a single run while it is executing; stop on a terminal status. */
export function runRefetchInterval(status: string | undefined): number | false {
  return status === 'running' ? 2000 : false
}

export function transformRunSummary(r: Record<string, unknown>): AnalysisRunSummary {
  return {
    id: r.id as string,
    reportType: (r.report_type as string) ?? 'quality',
    status: r.status as string,
    filters: (r.filters as AnalysisRunFilters) ?? {},
    error: (r.error as string) ?? null,
    provider: (r.provider as string) ?? null,
    model: (r.model as string) ?? null,
    promptTokens: (r.prompt_tokens as number) ?? 0,
    completionTokens: (r.completion_tokens as number) ?? 0,
    totalCostUsd: (r.total_cost_usd as number) ?? 0,
    conversationCount: (r.conversation_count as number) ?? 0,
    analyzedCount: (r.analyzed_count as number) ?? 0,
    skippedCount: (r.skipped_count as number) ?? 0,
    createdBy: (r.created_by as string) ?? null,
    createdAt: (r.created_at as string) ?? null,
    finishedAt: (r.finished_at as string) ?? null,
  }
}

export function transformRunDetail(r: Record<string, unknown>): AnalysisRunDetail {
  return {
    ...transformRunSummary(r),
    stats: (r.stats as AnalysisRunStats) ?? {},
    report: (r.report as AnalysisReport) ?? null,
    reportMarkdown: (r.report_markdown as string) ?? null,
  }
}

const BASE = '/api/conversation-analysis'

export const ANALYSIS_RUNS_KEY = ['conversation-analysis', 'runs'] as const

export function useAnalysisRuns(enabled = true) {
  return useQuery<AnalysisRunSummary[], Error>({
    queryKey: ANALYSIS_RUNS_KEY,
    queryFn: async () => {
      const rows = await apiClient.get<Record<string, unknown>[]>(`${BASE}/runs`)
      return rows.map(transformRunSummary)
    },
    enabled,
    refetchInterval: (query) => runsRefetchInterval(query.state.data),
  })
}

export function useAnalysisRun(runId: string | undefined, enabled = true) {
  return useQuery<AnalysisRunDetail, Error>({
    queryKey: ['conversation-analysis', 'runs', runId ?? ''],
    queryFn: async () =>
      transformRunDetail(
        await apiClient.get<Record<string, unknown>>(`${BASE}/runs/${runId}`),
      ),
    enabled: enabled && !!runId,
    refetchInterval: (query) => runRefetchInterval(query.state.data?.status),
  })
}

export function useCreateAnalysisRun() {
  const qc = useQueryClient()
  return useMutation<AnalysisRunDetail, Error, AnalysisRunCreateBody>({
    mutationFn: async (body) =>
      transformRunDetail(
        await apiClient.post<Record<string, unknown>>(`${BASE}/runs`, body),
      ),
    onSuccess: (run) => {
      // Prime the detail cache so navigating to the fresh run renders
      // instantly; `exact` keeps the invalidation off that primed entry
      // (the detail key shares this prefix).
      qc.setQueryData(['conversation-analysis', 'runs', run.id], run)
      qc.invalidateQueries({ queryKey: ANALYSIS_RUNS_KEY, exact: true })
    },
  })
}

export function useDeleteAnalysisRun() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: async (runId) => {
      await apiClient.delete(`${BASE}/runs/${runId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ANALYSIS_RUNS_KEY, exact: true })
    },
  })
}

/** id → display-name map from GET /api/admin/users (perm users:read; the
 * admin role has it). Consumers fall back to a truncated id — this lookup
 * failing must never block the page, hence retry: false. */
export function useAnalysisUserNames(enabled = true) {
  return useQuery<Record<string, string>, Error>({
    queryKey: ['conversation-analysis', 'users'],
    queryFn: async () => {
      const users = await apiClient.get<Record<string, unknown>[]>('/api/admin/users')
      const map: Record<string, string> = {}
      for (const u of users) {
        const id = u.id as string
        map[id] = (u.display_name as string) || (u.email as string) || id
      }
      return map
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function transformTrends(r: Record<string, unknown>): AnalysisTrends {
  return {
    bucket: (r.bucket as 'week' | 'month') ?? 'week',
    timezone: (r.timezone as string) ?? 'UTC',
    start: (r.start as string) ?? '',
    end: (r.end as string) ?? '',
    categories: (r.categories as string[]) ?? [],
    buckets: (r.buckets as TrendBucket[]) ?? [],
  }
}

/** Trends are stable between analysis runs — plain query, no polling. */
export function useAnalysisTrends(params: TrendsParams, enabled = true) {
  const { start, end, bucket, channels, personaIds } = params
  return useQuery<AnalysisTrends, Error>({
    queryKey: ['conversation-analysis', 'trends', start, end, bucket, channels, personaIds],
    queryFn: async () => {
      const search = new URLSearchParams({ start, end, bucket })
      for (const c of channels) search.append('channel', c)
      for (const p of personaIds) search.append('persona_id', p)
      return transformTrends(
        await apiClient.get<Record<string, unknown>>(`${BASE}/trends?${search}`),
      )
    },
    enabled: enabled && !!start && !!end && end >= start,
  })
}
